import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, PencilLine } from "lucide-react";

const FIELDS = [["name", "GP name", "e.g. Dr A. Patel"], ["practice", "Practice / surgery", "e.g. Govan Health Centre"], ["phone", "Phone", "e.g. 0141 000 0000"], ["email", "Email", "Optional"], ["address", "Address", "Street, town, postcode"]];
const BLANK = { name: "", practice: "", phone: "", email: "", address: "" };

export function GpCard({ gp, canEdit }) {
  const [cur, setCur] = useState({ ...BLANK, ...(gp || {}) });
  const [f, setF] = useState(cur);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const has = Object.values(cur).some(Boolean);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/family-portal/gp", f); setCur({ ...BLANK, ...data.gp }); setEditing(false); toast.success("GP details saved \u2014 the office has been told"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const inp = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand";
  return (
    <section data-testid="family-gp-card" className="rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-ink"><Stethoscope size={20} className="text-brand" /> GP details</h2>
        {canEdit && !editing && <button onClick={() => { setF(cur); setEditing(true); }} data-testid="family-gp-edit" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"><PencilLine size={14} /> {has ? "Edit" : "Add GP details"}</button>}
      </div>
      {editing ? (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map(([k, l, ph]) => (
              <label key={k} className={`text-xs font-semibold uppercase tracking-wider text-ink-muted ${k === "address" ? "sm:col-span-2" : ""}`}>{l}
                <input className={`${inp} mt-1 font-normal normal-case tracking-normal text-ink`} value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`family-gp-${k}`} />
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button disabled={busy} onClick={save} data-testid="family-gp-save" className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">Save GP details</button>
            <button onClick={() => setEditing(false)} data-testid="family-gp-cancel" className="rounded-lg border border-border px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : has ? (
        <dl className="grid gap-3 text-sm sm:grid-cols-2" data-testid="family-gp-details">
          {FIELDS.filter(([k]) => cur[k]).map(([k, l]) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}><dt className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{l}</dt><dd className="text-ink" data-testid={`family-gp-value-${k}`}>{cur[k]}</dd></div>
          ))}
        </dl>
      ) : <p className="text-sm text-ink-muted" data-testid="family-gp-empty">No GP details recorded yet.</p>}
    </section>
  );
}
